// The paginated-report service's client — project-overview's and issue-analysis's
// `src/utils/PaginatedReportGenerator.js` until 2026-10-20, one copy now (README.md).

// The service's one fixed endpoint: the report POST and the `?download=true&token=` GET.
const DEFAULT_ENDPOINT = 'https://paginated-report-service.rdrive.io/api/report';

// Bodies at or above this size (bytes) are gzipped before upload. Below it, the
// compression CPU isn't worth the tiny transfer saving, so we send plain JSON.
const GZIP_THRESHOLD_BYTES = 1024 * 1024; // 1 MB

// gzip a string into a Blob using the browser-native CompressionStream. Returns
// null if the browser lacks CompressionStream so callers can fall back to plain JSON.
async function gzipString(str) {
    if (typeof CompressionStream === 'undefined') return null;
    const stream = new Blob([str]).stream().pipeThrough(new CompressionStream('gzip'));
    return await new Response(stream).blob();
}

// The paginated-report service is NOT behind the rdrive proxy: it is a direct
// POST to its own fixed endpoint with the report in the body, and the download
// is a `?download=true&token=` URL it mints itself. It therefore needs no
// proxy fetcher and no ApiClient — it never used the one it used to be given.
class PaginatedReportGenerator {
    /**
     * @param {object} [options]
     * @param {string} [options.endpoint]  the service's endpoint; defaults to the
     *   production one (no caller passes it today)
     */
    constructor({ endpoint = DEFAULT_ENDPOINT } = {}) {
        this.reportEndpoint = endpoint;
        this.supportedFormats = [
            'PDF',
            'EXCELOPENXML',
            'WORDOPENXML',
            'CSV',
            'XML',
            'HTML5',
            'IMAGE'
        ];
    }

    async generateReport({
        reportName,
        fileFormat = 'PDF',
        parameters = [],
        data = {
            ItemsLatestData: [],
            ProjectInfo: [],
            Translation: []
        }
    }) {
        try {
            const normalizedFormat = fileFormat.toUpperCase();
            if (!this.supportedFormats.includes(normalizedFormat)) {
                throw new Error(`Unsupported file format. Supported formats are: ${this.supportedFormats.join(', ')}`);
            }

            const requestBody = {
                reportName,
                fileFormat: normalizedFormat,
                parameters,
                data
            };

            // Serialize once, then gzip only when the body is large enough to be worth
            // it. gzip both shrinks the upload (~10x for JSON) and keeps us under the
            // service's request-body limit (the source of the 413 Content Too Large).
            // NOTE: the report service must run request-decompression middleware
            // (ASP.NET: app.UseRequestDecompression()) to read gzipped bodies.
            const jsonBody = JSON.stringify(requestBody);
            const headers = { 'Content-Type': 'application/json' };
            let body = jsonBody;

            if (new Blob([jsonBody]).size >= GZIP_THRESHOLD_BYTES) {
                const gzipped = await gzipString(jsonBody);
                if (gzipped) {
                    body = gzipped;
                    headers['Content-Encoding'] = 'gzip';
                }
            }

            // First POST request to get the token
            const tokenResponse = await fetch(this.reportEndpoint, {
                method: 'POST',
                headers,
                body
            });

            if (!tokenResponse.ok) {
                throw new Error(`HTTP error! status: ${tokenResponse.status}`);
            }

            const { token } = await tokenResponse.json();

            // Create the download URL
            const downloadUrl = `${this.reportEndpoint}?download=true&token=${token}`;

            // Create an invisible iframe for download
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = downloadUrl;

            // Add necessary attributes for security policy. `allow` is ONE
            // permissions-policy list: issue-analysis's copy set 'downloads' and then
            // overwrote it with 'clipboard-write', project-overview's set 'downloads' only.
            iframe.setAttribute('sandbox', 'allow-downloads allow-scripts allow-same-origin');
            iframe.setAttribute('allow', 'downloads; clipboard-write');

            // Handle cleanup
            iframe.onload = () => {
                setTimeout(() => {
                    document.body.removeChild(iframe);
                }, 5000); // Remove iframe after 5 seconds
            };

            document.body.appendChild(iframe);

        } catch (error) {
            console.error('Error generating report:', error);
            throw new Error('Failed to generate report: ' + error.message);
        }
    }
}

export { PaginatedReportGenerator };
export default PaginatedReportGenerator;
